import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';

function genCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const seg = (n) => Array.from({length:n}, () => chars[Math.floor(Math.random()*chars.length)]).join('');
  return `BUDDHA-${seg(4)}-${seg(4)}`;
}

export default function AdminPanel() {
  const [tab, setTab] = useState('codes');
  const [codes, setCodes] = useState([]);
  const [users, setUsers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [allTrades, setAllTrades] = useState([]);
  const [aiUsage, setAiUsage] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copied, setCopied] = useState('');

  // Stats
  const activeUsers = users.filter(u => u.access_status === 'active').length;
  const activeCodes = codes.filter(c => c.is_active && !c.used_at).length;
  const usedCodes = codes.filter(c => c.used_at).length;
  const pendingReqs = requests.filter(r => r.status === 'pending').length;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [codesRes, usersRes, reqsRes, tradesRes, aiRes] = await Promise.all([
      supabase.from('invite_codes').select('*').order('created_at', {ascending: false}),
      supabase.from('profiles').select('*').order('created_at', {ascending: false}),
      supabase.from('access_requests').select('*').order('created_at', {ascending: false}),
      supabase.from('trades').select('*').order('trade_date', {ascending: false}),
      supabase.from('ai_usage').select('*').order('usage_date', {ascending: false}).limit(500),
    ]);
    setLoading(false);
    if (!codesRes.error) setCodes(codesRes.data || []);
    if (!usersRes.error) setUsers(usersRes.data || []);
    if (!reqsRes.error) setRequests(reqsRes.data || []);
    if (!tradesRes.error) setAllTrades(tradesRes.data || []);
    if (!aiRes.error) setAiUsage(aiRes.data || []);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  async function createCode() {
    setError(''); setSuccess('');
    const code = newCode.trim().toUpperCase() || genCode();
    const { error } = await supabase.from('invite_codes').insert({ code, plan: 'lifetime', is_active: true });
    if (error) { setError(error.message); return; }
    setSuccess(`Code created: ${code}`);
    setNewCode('');
    fetchAll();
  }

  async function deactivateCode(id) {
    await supabase.from('invite_codes').update({ is_active: false }).eq('id', id);
    fetchAll();
  }

  async function approveRequest(req) {
    const code = genCode();
    const { error: codeErr } = await supabase.from('invite_codes').insert({ code, plan: 'lifetime', is_active: true });
    if (codeErr) { setError(codeErr.message); return; }
    await supabase.from('access_requests').update({ status: 'approved', responded_at: new Date().toISOString() }).eq('id', req.id);
    setSuccess(`Approved ${req.email}. Code: ${code} — email this to them manually.`);
    fetchAll();
  }

  async function rejectRequest(id) {
    await supabase.from('access_requests').update({ status: 'rejected', responded_at: new Date().toISOString() }).eq('id', id);
    fetchAll();
  }

  function copyCode(code) {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(code);
      setTimeout(() => setCopied(''), 2000);
    });
  }

  const tradeCountByUser = {};
  allTrades.forEach(t => { tradeCountByUser[t.user_id] = (tradeCountByUser[t.user_id] || 0) + 1; });

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title"><em>Admin Panel</em></h1>
        <p className="page-subtitle">Manage invite codes, users, and access requests</p>
      </div>

      {/* STATS */}
      <div className="admin-stats">
        <div className="admin-stat"><div className="admin-stat-val">{users.length}</div><div className="admin-stat-label">Total Users</div></div>
        <div className="admin-stat"><div className="admin-stat-val">{activeUsers}</div><div className="admin-stat-label">Active Users</div></div>
        <div className="admin-stat"><div className="admin-stat-val">{activeCodes}</div><div className="admin-stat-label">Unused Codes</div></div>
        <div className="admin-stat"><div className="admin-stat-val">{usedCodes}</div><div className="admin-stat-label">Used Codes</div></div>
        <div className="admin-stat"><div className="admin-stat-val">{allTrades.length}</div><div className="admin-stat-label">Total Trades</div></div>
        <div className="admin-stat"><div className="admin-stat-val">{pendingReqs}</div><div className="admin-stat-label">Pending Requests</div></div>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {success && <div className="success-banner" style={{wordBreak:'break-all'}}>{success}</div>}

      {/* TABS */}
      <div className="admin-tabs">
        {['codes','users','requests','trades','aiusage'].map(t => (
          <button key={t} className={`admin-tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
            {t === 'codes' ? 'Invite Codes' : t === 'users' ? 'Users' : t === 'requests' ? `Access Requests${pendingReqs > 0 ? ` (${pendingReqs})` : ''}` : t === 'trades' ? 'All Trades' : 'AI Usage'}
          </button>
        ))}
      </div>

      {loading && <div style={{textAlign:'center',padding:32,color:'var(--text-3)'}}>Loading...</div>}

      {/* INVITE CODES TAB */}
      {tab === 'codes' && !loading && (
        <div>
          <div className="create-code-form">
            <input
              placeholder="Leave empty to auto-generate (BUDDHA-XXXX-XXXX)"
              value={newCode}
              onChange={e => setNewCode(e.target.value.toUpperCase())}
              style={{fontFamily:'DM Mono,monospace', letterSpacing:'1px'}}
            />
            <button className="btn-gold btn-sm" style={{padding:'10px 20px'}} onClick={createCode}>Create Code</button>
            <button className="btn-sm ghost" onClick={() => setNewCode(genCode())}>Auto-Generate</button>
          </div>
          <div className="trades-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Status</th>
                  <th>Plan</th>
                  <th>Created</th>
                  <th>Used</th>
                  <th>Used By</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {codes.map(c => (
                  <tr key={c.id}>
                    <td style={{fontFamily:'DM Mono,monospace',letterSpacing:'1px'}}>{c.code}</td>
                    <td>
                      <span className={`badge ${!c.is_active ? 'red' : c.used_at ? 'grey' : 'green'}`}>
                        {!c.is_active ? 'Inactive' : c.used_at ? 'Used' : 'Active'}
                      </span>
                    </td>
                    <td className="td-mono">{c.plan}</td>
                    <td style={{color:'var(--text-3)',fontSize:12}}>{c.created_at?.slice(0,10)}</td>
                    <td style={{color:'var(--text-3)',fontSize:12}}>{c.used_at?.slice(0,10) || '—'}</td>
                    <td style={{color:'var(--text-3)',fontSize:11}}>{c.used_by ? c.used_by.slice(0,8)+'...' : '—'}</td>
                    <td style={{display:'flex',gap:6}}>
                      <button className="btn-sm ghost" onClick={() => copyCode(c.code)}>
                        {copied === c.code ? 'Copied!' : 'Copy'}
                      </button>
                      {c.is_active && !c.used_at && (
                        <button className="btn-sm danger" onClick={() => deactivateCode(c.id)}>Deactivate</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* USERS TAB */}
      {tab === 'users' && !loading && (
        <div className="trades-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Subscription</th>
                <th>Renews</th>
                <th>Joined</th>
                <th>Trades</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const subActive = ['active','authenticated'].includes(u.subscription_status) &&
                  u.subscription_current_period_end &&
                  new Date(u.subscription_current_period_end) > new Date();
                return (
                  <tr key={u.id}>
                    <td>{u.full_name || '—'}</td>
                    <td style={{fontFamily:'DM Mono,monospace',fontSize:12}}>{u.email}</td>
                    <td>
                      <span className={`badge ${u.role === 'admin' ? 'gold' : 'grey'}`}>{u.role}</span>
                    </td>
                    <td>
                      <span className={`badge ${subActive ? 'green' : ['halted','pending','created'].includes(u.subscription_status) ? 'amber' : ['cancelled','completed','expired'].includes(u.subscription_status) ? 'red' : 'grey'}`}>
                        {u.subscription_status || 'none'}
                      </span>
                    </td>
                    <td style={{color:'var(--text-3)',fontSize:12}}>
                      {u.subscription_current_period_end
                        ? new Date(u.subscription_current_period_end).toLocaleDateString('en-IN', {day:'numeric',month:'short'})
                        : '—'}
                    </td>
                    <td style={{color:'var(--text-3)',fontSize:12}}>{u.created_at?.slice(0,10)}</td>
                    <td className="td-mono">{tradeCountByUser[u.id] || 0}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ACCESS REQUESTS TAB */}
      {tab === 'requests' && !loading && (
        <div className="trades-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Message</th>
                <th>Submitted</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map(r => (
                <tr key={r.id}>
                  <td>{r.full_name || '—'}</td>
                  <td style={{fontFamily:'DM Mono,monospace',fontSize:12}}>{r.email}</td>
                  <td style={{maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',color:'var(--text-3)',fontSize:12}}>{r.message || '—'}</td>
                  <td style={{color:'var(--text-3)',fontSize:12}}>{r.created_at?.slice(0,10)}</td>
                  <td>
                    <span className={`badge ${r.status === 'approved' ? 'green' : r.status === 'rejected' ? 'red' : 'amber'}`}>
                      {r.status}
                    </span>
                  </td>
                  <td style={{display:'flex',gap:6}}>
                    {r.status === 'pending' && (
                      <>
                        <button className="btn-sm gold" onClick={() => approveRequest(r)}>Approve</button>
                        <button className="btn-sm danger" onClick={() => rejectRequest(r.id)}>Reject</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {requests.length === 0 && (
                <tr><td colSpan="6" style={{textAlign:'center',color:'var(--text-4)',padding:'32px'}}>No access requests yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ALL TRADES TAB */}
      {tab === 'trades' && !loading && (
        <div className="trades-table-wrap">
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Date</th>
                <th>Dir</th>
                <th>Entry</th>
                <th>Stop</th>
                <th>Exit</th>
                <th>R</th>
                <th>Outcome</th>
                <th>Setup</th>
              </tr>
            </thead>
            <tbody>
              {allTrades.map(t => {
                const u = users.find(u => u.id === t.user_id);
                return (
                  <tr key={t.id}>
                    <td style={{fontSize:12,color:'var(--text-3)'}}>{u?.email?.split('@')[0] || t.user_id.slice(0,8)}</td>
                    <td className="td-mono">{t.trade_date}</td>
                    <td><span className={`badge ${t.direction === 'Long' ? 'green' : 'red'}`}>{t.direction}</span></td>
                    <td className="td-mono">{t.entry_price}</td>
                    <td className="td-mono">{t.stop_loss}</td>
                    <td className="td-mono">{t.exit_price}</td>
                    <td className={t.r_multiple > 0 ? 'td-pos' : t.r_multiple < 0 ? 'td-neg' : 'td-mono'}>
                      {t.r_multiple !== null ? `${t.r_multiple}R` : '—'}
                    </td>
                    <td><span className={`badge ${t.outcome === 'Win' ? 'green' : t.outcome === 'Loss' ? 'red' : 'grey'}`}>{t.outcome}</span></td>
                    <td style={{fontSize:12,color:'var(--text-3)'}}>{t.setup_type || '—'}</td>
                  </tr>
                );
              })}
              {allTrades.length === 0 && (
                <tr><td colSpan="9" style={{textAlign:'center',color:'var(--text-4)',padding:32}}>No trades logged yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* AI USAGE TAB */}
      {tab === 'aiusage' && !loading && (() => {
        // Aggregate: total today, total this week, total this month, per-user breakdown
        const today = new Date().toISOString().slice(0, 10);
        const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 7);
        const weekAgoStr = weekAgo.toISOString().slice(0, 10);

        const todayTotal = aiUsage.filter(u => u.usage_date === today).reduce((s, u) => s + u.count, 0);
        const weekTotal = aiUsage.filter(u => u.usage_date >= weekAgoStr).reduce((s, u) => s + u.count, 0);
        const allTimeTotal = aiUsage.reduce((s, u) => s + u.count, 0);
        const activeUsersToday = new Set(aiUsage.filter(u => u.usage_date === today).map(u => u.user_id)).size;

        // Per-user totals
        const byUser = {};
        aiUsage.forEach(u => {
          if (!byUser[u.user_id]) byUser[u.user_id] = { total: 0, today: 0, week: 0, lastUsed: null };
          byUser[u.user_id].total += u.count;
          if (u.usage_date === today) byUser[u.user_id].today += u.count;
          if (u.usage_date >= weekAgoStr) byUser[u.user_id].week += u.count;
          if (!byUser[u.user_id].lastUsed || u.last_used > byUser[u.user_id].lastUsed) byUser[u.user_id].lastUsed = u.last_used;
        });

        const sortedUsers = Object.entries(byUser)
          .map(([uid, stats]) => ({ uid, ...stats, user: users.find(u => u.id === uid) }))
          .sort((a, b) => b.total - a.total);

        return (
          <div>
            <div className="admin-stats" style={{marginBottom:20}}>
              <div className="admin-stat"><div className="admin-stat-val">{todayTotal}</div><div className="admin-stat-label">AI Calls Today</div></div>
              <div className="admin-stat"><div className="admin-stat-val">{weekTotal}</div><div className="admin-stat-label">AI Calls This Week</div></div>
              <div className="admin-stat"><div className="admin-stat-val">{allTimeTotal}</div><div className="admin-stat-label">All-Time AI Calls</div></div>
              <div className="admin-stat"><div className="admin-stat-val">{activeUsersToday}</div><div className="admin-stat-label">Active AI Users Today</div></div>
            </div>

            <h3 style={{fontFamily:'Bebas Neue,sans-serif',fontSize:14,letterSpacing:2,color:'var(--text-3)',marginBottom:12}}>
              USAGE BY USER
            </h3>
            <div className="trades-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Today</th>
                    <th>Last 7 Days</th>
                    <th>All Time</th>
                    <th>Last Used</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedUsers.map(row => (
                    <tr key={row.uid}>
                      <td>{row.user?.full_name || '—'}</td>
                      <td style={{fontFamily:'DM Mono,monospace',fontSize:12,color:'var(--text-3)'}}>{row.user?.email || row.uid.slice(0,8)}</td>
                      <td className="td-mono" style={{color: row.today >= 15 ? 'var(--amber)' : 'var(--text-1)'}}>{row.today}</td>
                      <td className="td-mono">{row.week}</td>
                      <td className="td-mono" style={{color:'var(--gold-2)'}}>{row.total}</td>
                      <td style={{fontSize:11,color:'var(--text-3)'}}>{row.lastUsed?.slice(0, 16).replace('T', ' ') || '—'}</td>
                    </tr>
                  ))}
                  {sortedUsers.length === 0 && (
                    <tr><td colSpan="6" style={{textAlign:'center',color:'var(--text-4)',padding:32}}>No AI usage yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
